import React from "react";
import {
    Box,
    Grid,
    Typography,
    Paper,
    Card,
    CardContent,
    Avatar,
    LinearProgress,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
} from "@mui/material";
import {
    BookOpen,
    Clock,
    CheckCircle,
    TrendingUp,
    Award,
    Calendar,
    ChevronRight,
    MoreVertical,
    PlayCircle,
    FileText,
    Users,
} from "lucide-react";
import { motion } from "framer-motion";
import StatsCard from "../../Components/StatsCard/StatsCard";

const TutoringDashboard = () => {
    const stats = [
        {
            title: "Course Progress",
            lastMonth: "40%",
            thisMonth: "65%",
            multiplier: "1.62x",
            percentage: "62.50%",
            isIncrease: true,
            theme: "purple" as const,
        },
        {
            title: "Time Spent",
            lastMonth: "12.5h",
            thisMonth: "24.5h",
            multiplier: "1.96x",
            percentage: "96.00%",
            isIncrease: true,
            theme: "teal" as const,
        },
        {
            title: "Completed Tasks",
            lastMonth: "8/18",
            thisMonth: "12/18",
            multiplier: "1.50x",
            percentage: "50.00%",
            isIncrease: true,
            theme: "green" as const,
        },
        {
            title: "Average Score",
            lastMonth: "82%",
            thisMonth: "88%",
            multiplier: "1.07x",
            percentage: "7.32%",
            isIncrease: true,
            theme: "orange" as const,
        },
    ];

    const recentCourses = [
        {
            id: 1,
            title: "Advanced Algebra & Calculus",
            progress: 75,
            tutor: "Dr. John Smith",
            image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&q=80&w=400",
            nextLesson: "Differential Equations",
            time: "Tomorrow, 10:30 AM",
        },
        {
            id: 2,
            title: "Quantum Physics Fundamentals",
            progress: 40,
            tutor: "Prof. Emily Davis",
            image: "https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa?auto=format&fit=crop&q=80&w=400",
            nextLesson: "Wave-Particle Duality",
            time: "Friday, 2:00 PM",
        },
    ];

    const upcomingClasses = [
        { id: 1, title: "Mathematics Workshop", time: "10:30 AM - 11:30 AM", date: "Today" },
        { id: 2, title: "Physics Deep Dive", time: "02:00 PM - 03:30 PM", date: "Jan 25" },
        { id: 3, title: "Computer Science Lab", time: "09:00 AM - 11:00 AM", date: "Jan 26" },
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
            },
        },
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
        },
    };

    return (
        <Box sx={{ p: 4, bgcolor: "#f8fafc", minHeight: "100vh" }}>
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Header */}
                {/* <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: "#1e293b", mb: 1 }}>
                            Student Dashboard
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#64748b" }}>
                            Welcome back! Here's what's happening with your learning journey today.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<Award />}
                        sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            px: 3,
                            py: 1.5,
                            bgcolor: "#4f46e5",
                            boxShadow: "0 4px 14px rgba(79, 70, 229, 0.4)",
                            "&:hover": { bgcolor: "#4338ca" }
                        }}
                    >
                        View Achievements
                    </Button>
                </Box> */}

                {/* Stats Grid */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {stats.map((stat, idx) => (
                        <Grid item xs={12} sm={6} md={3} key={idx}>
                            <StatsCard
                                title={stat.title}
                                lastMonthValue={stat.lastMonth}
                                thisMonthValue={stat.thisMonth}
                                multiplier={stat.multiplier}
                                percentage={stat.percentage}
                                isIncrease={stat.isIncrease}
                                themeColor={stat.theme}
                                variants={itemVariants}
                            />
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={4}>
                    {/* Main Content: Courses */}
                    <Grid item xs={12} lg={8}>
                        <motion.div variants={itemVariants}>
                            <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                    Ongoing Courses
                                </Typography>
                                <Button endIcon={<ChevronRight size={18} />} sx={{ textTransform: "none", color: "#4f46e5", fontWeight: 600 }}>
                                    View All
                                </Button>
                            </Box>

                            <Grid container spacing={3}>
                                {recentCourses.map((course) => (
                                    <Grid item xs={12} md={6} key={course.id}>
                                        <Card sx={{
                                            borderRadius: "24px",
                                            overflow: "hidden",
                                            border: "1px solid rgba(0,0,0,0.05)",
                                            boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
                                            "&:hover": { boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }
                                        }}>
                                            <Box sx={{ position: "relative", height: 160 }}>
                                                <img
                                                    src={course.image}
                                                    alt={course.title}
                                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                />
                                                <Box sx={{
                                                    position: "absolute",
                                                    bottom: 12,
                                                    left: 12,
                                                    bgcolor: "rgba(255,255,255,0.9)",
                                                    backdropFilter: "blur(4px)",
                                                    p: "6px 12px",
                                                    borderRadius: "10px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1
                                                }}>
                                                    <Users size={14} color="#4f46e5" />
                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#1e293b" }}>
                                                        {course.tutor}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <CardContent sx={{ p: 3 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: "#1e293b", fontSize: "1.1rem" }}>
                                                    {course.title}
                                                </Typography>
                                                <Box sx={{ mb: 2.5 }}>
                                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                                        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>Progress</Typography>
                                                        <Typography variant="caption" sx={{ color: "#1e293b", fontWeight: 700 }}>{course.progress}%</Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={course.progress}
                                                        sx={{
                                                            height: 8,
                                                            borderRadius: 4,
                                                            bgcolor: "#e2e8f0",
                                                            "& .MuiLinearProgress-bar": { bgcolor: "#4f46e5", borderRadius: 4 }
                                                        }}
                                                    />
                                                </Box>
                                                <Box sx={{
                                                    p: 1.5,
                                                    borderRadius: "16px",
                                                    bgcolor: "#f1f5f9",
                                                    mb: 2,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1.5
                                                }}>
                                                    <Box sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: "8px",
                                                        bgcolor: "#fff",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        boxShadow: "0 2px 4px rgba(0,0,0,0.03)"
                                                    }}>
                                                        <PlayCircle size={18} color="#4f46e5" />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ display: "block", color: "#64748b", fontSize: "10px", fontWeight: 600, textTransform: "uppercase" }}>
                                                            Next Lesson
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                                                            {course.nextLesson}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    sx={{
                                                        borderRadius: "12px",
                                                        textTransform: "none",
                                                        borderColor: "#e2e8f0",
                                                        color: "#1e293b",
                                                        fontWeight: 600,
                                                        "&:hover": { borderColor: "#4f46e5", color: "#4f46e5" }
                                                    }}
                                                >
                                                    Continue Learning
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </motion.div>
                    </Grid>

                    {/* Sidebar: Upcoming & Tasks */}
                    <Grid item xs={12} lg={4}>
                        <motion.div variants={itemVariants}>
                            <Paper sx={{ p: 3, borderRadius: "24px", bgcolor: "#fff", border: "1px solid rgba(0,0,0,0.05)", mb: 4 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                        Upcoming Classes
                                    </Typography>
                                    <IconButton size="small"><MoreVertical size={18} /></IconButton>
                                </Box>
                                <List disablePadding>
                                    {upcomingClasses.map((item, idx) => (
                                        <ListItem
                                            key={item.id}
                                            sx={{
                                                px: 0,
                                                py: 2,
                                                borderBottom: idx < upcomingClasses.length - 1 ? "1px solid #f1f5f9" : "none",
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: 2
                                            }}
                                        >
                                            <Box sx={{
                                                p: 1,
                                                borderRadius: "10px",
                                                bgcolor: item.date === "Today" ? "#eef2ff" : "#f8fafc",
                                                color: item.date === "Today" ? "#4f46e5" : "#64748b",
                                                textAlign: "center",
                                                minWidth: 48
                                            }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, display: "block", fontSize: "10px", textTransform: "uppercase" }}>
                                                    {item.date}
                                                </Typography>
                                                <Calendar size={16} />
                                            </Box>
                                            <ListItemText
                                                primary={item.title}
                                                secondary={item.time}
                                                primaryTypographyProps={{ sx: { fontWeight: 700, color: "#1e293b", fontSize: "0.95rem" } }}
                                                secondaryTypographyProps={{ sx: { color: "#64748b", fontWeight: 500 } }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        mt: 2,
                                        borderRadius: "12px",
                                        bgcolor: "#f1f5f9",
                                        color: "#1e293b",
                                        textTransform: "none",
                                        fontWeight: 700,
                                        boxShadow: "none",
                                        "&:hover": { bgcolor: "#e2e8f0", boxShadow: "none" }
                                    }}
                                >
                                    Open Calendar
                                </Button>
                            </Paper>

                            <Paper sx={{ p: 3, borderRadius: "24px", bgcolor: "#fff", border: "1px solid rgba(0,0,0,0.05)" }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
                                    Quick Tasks
                                </Typography>
                                {[
                                    { label: "Submit Math Assignment", type: "assignment", due: "2h left" },
                                    { label: "Revise Quantum Notes", type: "note", due: "Tomorrow" },
                                ].map((task, idx) => (
                                    <Box key={idx} sx={{
                                        p: 2,
                                        borderRadius: "16px",
                                        bgcolor: "#f8fafc",
                                        mb: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2
                                    }}>
                                        <Box sx={{ color: "#4f46e5" }}>
                                            {task.type === "assignment" ? <FileText size={20} /> : <Award size={20} />}
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>{task.label}</Typography>
                                            <Typography variant="caption" sx={{ color: "#ef4444", fontWeight: 700 }}>{task.due}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Paper>
                        </motion.div>
                    </Grid>
                </Grid>
            </motion.div>
        </Box>
    );
};

export default TutoringDashboard;
